import { useMutation } from "@tanstack/react-query";
import { isValid } from "date-fns";
import { Archive, ArchiveRestore } from "lucide-react";
import {
  InfiniteListBase,
  ShowBase,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useListContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { getDealsByStage } from "./stages";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { EmailList } from "../emails/EmailList";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { ContactList } from "./ContactList";
import { findDealLabel, formatISODateString } from "./dealUtils";
import { DealLocation } from "../locations/LocationInfo";
import { DealDocuments } from "./DealDocuments";
import { DealChannel } from "./DealChannel";
import { DealSeries } from "./DealSeries";
import { DealParticipants } from "./DealParticipants";

export const DealShow = ({ open, id }: { open: boolean; id?: string }) => {
  const redirect = useRedirect();
  const handleClose = () => {
    redirect("list", "deals");
  };
  useDealKeyboardNavigation(open, id);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="lg:max-w-4xl p-4 overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        {id ? (
          <ShowBase id={id}>
            <DealShowContent />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// LDF: Im geöffneten Auftrag mit den Pfeiltasten durchs Board blättern:
// ↑ ↓ vorheriger/nächster Auftrag in derselben Phase, ← → gleiche Position in der vorigen/nächsten Phase mit Einträgen.
const useDealKeyboardNavigation = (open: boolean, id?: string) => {
  const { data } = useListContext<Deal>();
  const { dealStages } = useConfigurationContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !id || !data?.length) return;
    const byStage = getDealsByStage(data, dealStages);
    const columns = dealStages.map((s) => byStage[s.value] ?? []);
    const col = columns.findIndex((c) => c.some((d) => String(d.id) === String(id)));
    if (col < 0) return;
    const row = columns[col].findIndex((d) => String(d.id) === String(id));

    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      // Nur der Auftrag selbst ist offen – bei weiterem Fenster (Hilfe, Bearbeiten, Menü) nichts tun
      if (document.querySelectorAll('[role="dialog"][data-state="open"], [role="menu"]').length > 1) return;
      if (target?.closest('[role="listbox"], [role="menu"], [role="tablist"]')) return;
      let next: Deal | undefined;
      if (event.key === "ArrowDown") next = columns[col][row + 1];
      else if (event.key === "ArrowUp") next = columns[col][row - 1];
      else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        const step = event.key === "ArrowRight" ? 1 : -1;
        for (let c = col + step; c >= 0 && c < columns.length; c += step) {
          if (columns[c].length) {
            next = columns[c][Math.min(row, columns[c].length - 1)];
            break;
          }
        }
      } else return;
      event.preventDefault();
      if (next) navigate(`/deals/${next.id}/show`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, id, data, dealStages, navigate]);
};

export const DealShowContent = ({ mobile = false }: { mobile?: boolean }) => {
  const translate = useTranslate();
  const { dealStages, dealCategories, currency } = useConfigurationContext();
  const record = useRecordContext<Deal>();
  if (!record) return null;

  return (
    <>
      <div className="space-y-2">
        {record.archived_at ? <ArchivedTitle /> : null}
        <div className="flex-1">
          <div className={`flex justify-between items-start ${mobile ? "mb-4 flex-wrap gap-3" : "mb-8"}`}>
            <div className="flex items-center gap-4">
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              >
                <CompanyAvatar />
              </ReferenceField>
              <div>
                <h2 className={mobile ? "text-xl font-semibold" : "text-2xl font-semibold"}>{record.name}</h2>
                {!mobile && <span className="text-xs text-muted-foreground">↑ ↓ Aufträge in dieser Phase · ← → vorige/nächste Phase</span>}
              </div>
            </div>
            <div className={`flex gap-2 ${record.archived_at || mobile ? "" : "pr-12"}`}>
              {record.archived_at ? (
                <>
                  <UnarchiveButton record={record} />
                  <DeleteButton />
                </>
              ) : (
                <>
                  <ArchiveButton record={record} />
                  {/* Bearbeiten gibt es (noch) nur am Desktop */}
                  {!mobile && <EditButton />}
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-3 m-4">
            <div className="flex flex-col mr-10">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.expected_closing_date")}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {record.expected_closing_date && isValid(new Date(record.expected_closing_date))
                    ? formatISODateString(record.expected_closing_date)
                    : translate("resources.deals.invalid_date")}
                </span>
                {record.expected_closing_date && new Date(record.expected_closing_date) < new Date() ? (
                  <Badge variant="destructive">
                    {translate("crm.common.past")}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col mr-10">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.amount")}
              </span>
              <span className="text-sm">
                {/* LDF: Betrag kann leer sein (z. B. Rechnung fehlt) */}
                {record.amount == null
                  ? "–"
                  : record.amount.toLocaleString("de-DE", {
                      notation: "compact",
                      style: "currency",
                      currency,
                      currencyDisplay: "narrowSymbol",
                      minimumSignificantDigits: 3,
                    })}
              </span>
            </div>

            {record.category && (
              <div className="flex flex-col mr-10">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.category")}
                </span>
                <span className="text-sm">
                  {dealCategories.find((c) => c.value === record.category)
                    ?.label ?? record.category}
                </span>
              </div>
            )}

            <div className="flex flex-col mr-10">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.stage")}
              </span>
              <span className="text-sm">
                {findDealLabel(dealStages, record.stage)}
              </span>
            </div>
          </div>

          <DealChannel record={record} />

          <DealParticipants dealId={record.id} />

          <DealSeries record={record} />

          {!!record.contact_ids?.length && (
            <div className="m-4">
              <div className="flex flex-col min-h-12 mr-10">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.contact_ids")}
                </span>
                <ReferenceArrayField
                  source="contact_ids"
                  reference="contacts_summary"
                >
                  <ContactList />
                </ReferenceArrayField>
              </div>
            </div>
          )}

          {record.description && (
            <div className="m-4 whitespace-pre-line">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.description")}
              </span>
              <p className="text-sm leading-6">{record.description}</p>
            </div>
          )}

          <div className="m-4">
            <Separator className="mb-4" />
            <InfiniteListBase
              resource="deal_notes"
              filter={{ deal_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={<NoteCreate reference={"deals"} />}
            >
              <NotesIterator reference="deals" />
            </InfiniteListBase>
          </div>

          <DealLocation locationId={record.location_company_id} eventDate={record.event_date} />

          <div className="m-4">
            <DealDocuments dealId={record.id} />
          </div>

          <div className="m-4">
            <Separator className="mb-4" />
            <span className="text-xs text-muted-foreground tracking-wide">
              E-Mails zu diesem Auftrag und seiner Firma
            </span>
            <EmailList
              scope={{ type: "deal", id: record.id, companyId: record.company_id }}
              emptyText="Keine E-Mails zu diesem Auftrag oder der Firma."
            />
          </div>
        </div>
      </div>
    </>
  );
};

const ArchivedTitle = () => {
  const translate = useTranslate();
  return (
    <div className="bg-orange-500 px-6 py-4">
      <h3 className="text-lg font-bold text-white">
        {translate("resources.deals.archived.title")}
      </h3>
    </div>
  );
};

const ArchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleClick = () => {
    update(
      "deals",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          redirect("list", "deals");
          notify("resources.deals.archived.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.archived.error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <Archive className="w-4 h-4" />
      {translate("resources.deals.archived.action")}
    </Button>
  );
};

const UnarchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate } = useMutation({
    mutationFn: () => dataProvider.unarchiveDeal(record),
    onSuccess: () => {
      redirect("list", "deals");
      notify("resources.deals.unarchived.success", {
        type: "info",
        undoable: false,
      });
      refresh();
    },
    onError: () => {
      notify("resources.deals.unarchived.error", {
        type: "error",
      });
    },
  });

  const handleClick = () => {
    mutate();
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <ArchiveRestore className="w-4 h-4" />
      {translate("resources.deals.unarchived.action")}
    </Button>
  );
};
